import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { exportHistoricToExcel } from "../utils/exportHistoric";
import ModalVistaPrevia from "../documento/ModalVistaPrevia";
import { documentoDesdeTabla } from "../documento/desdeTabla";
import { descargarPdf } from "../documento/pdf";
import type { Bloque } from "../documento/tipos";
import type { ReportTableColumn } from "./ReportPointsTable";
import {
  trackReportExported,
  trackReportExportFailed,
} from "../../../analytics/reportsAnalytics";

// Metadata for the exported file. `fileBase` is the name WITHOUT extension; the
// component appends `.xlsx` / `.pdf` per format.
export interface ExportMetaInput {
  title: string;
  period: string;
  fileBase: string;
  /** Cliente y unidad, para la linea del encabezado del documento. */
  subtitle?: string;
  /** La pildora del encabezado. Sin un dato que la justifique, no va. */
  stamp?: string;
}

interface ExportButtonsProps<T extends { id: string }> {
  rows: T[];
  columns: ReportTableColumn<T>[];
  meta: ExportMetaInput;
  // Extra disable condition on top of "no rows" / "already generating".
  disabled?: boolean;
  // Override the failure handling. When omitted, a default error dialog is
  // shown so a failed export is never silent.
  onError?: (err: unknown) => void;
  className?: string;
  /**
   * Pantalla que monta los botones, para atribuir el evento de exportacion.
   *
   * Es una etiqueta corta y fija (`reports_history`, `truck_yard_access`), no
   * el titulo del reporte: ese se arma con datos del cliente.
   */
  analyticsSource?: string;
  /**
   * Bloques propios de la seccion —relojes, una ficha, una nota— que se
   * insertan entre el resumen y la tabla.
   *
   * Existe para que una seccion pueda enriquecer su entregable sin que este
   * componente tenga que saber de que reporte se trata.
   */
  extraBlocks?: (Bloque | null | false)[];
}

// Excel + PDF download pair, reusing the reports' `exportHistoric` utility so the
// styling/paging/chunking logic lives in one place. Buttons disable themselves
// when there is nothing to export or while a file is being generated. The whole
// currently-filtered `rows` set is exported — not just the visible page.
export default function ExportButtons<T extends { id: string }>({
  rows,
  columns,
  meta,
  disabled = false,
  onError,
  className = "",
  analyticsSource = "unknown",
  extraBlocks = [],
}: ExportButtonsProps<T>) {
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [previa, setPrevia] = useState(false);
  const noData = rows.length === 0;
  const blocked = disabled || noData || exporting !== null;

  // La fecha se congela al montar: si se recalculara en cada render, la hoja
  // que se ve y la que se descarga podrian quedar con minutos distintos.
  const generado = useMemo(
    () => new Date().toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }),
    [],
  );

  /*
    El documento es lo que se previsualiza Y lo que se imprime: construirlo una
    sola vez es lo que garantiza que no puedan separarse. Se recalcula solo
    cuando cambian las filas, las columnas o los bloques de la seccion.
  */
  const documento = useMemo(
    () =>
      documentoDesdeTabla(rows, columns, {
        titulo: meta.title,
        periodo: meta.period,
        archivo: meta.fileBase,
        subtitulo: meta.subtitle,
        sello: meta.stamp,
      }, extraBlocks),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, columns, meta.title, meta.period, meta.fileBase, meta.subtitle, meta.stamp, extraBlocks],
  );

  const onDownload = async (type: "excel" | "pdf") => {
    if (noData || exporting !== null) return;
    setExporting(type);
    // Yield a tick so the spinner paints before the (potentially heavy) build.
    await new Promise((r) => setTimeout(r, 50));
    try {
      const fullMeta = {
        title: meta.title,
        period: meta.period,
        fileName: `${meta.fileBase}.${type === "excel" ? "xlsx" : "pdf"}`,
      };
      if (type === "excel") {
        await exportHistoricToExcel(rows, columns, fullMeta);
      } else {
        await descargarPdf(documento, generado);
      }
      // `rowCount` es la metrica que importa: las quejas de "se traba al
      // exportar" hoy no se pueden correlacionar con el tamaño de la tabla.
      // Las filas NO viajan: traen conductor, placa y direcciones.
      trackReportExported({
        source: analyticsSource,
        format: type,
        rowCount: rows.length,
        columnCount: columns.length,
      });
    } catch (err) {
      trackReportExportFailed({
        source: analyticsSource,
        format: type,
        rowCount: rows.length,
      });
      console.error("[ExportButtons] export failed", err);
      if (onError) {
        onError(err);
      } else {
        Swal.fire({
          icon: "error",
          title: "No se pudo generar el archivo",
          text:
            err instanceof Error
              ? err.message
              : "Ocurrió un error al exportar los datos. Inténtalo de nuevo.",
        });
      }
    } finally {
      setExporting(null);
    }
  };

  const excelDesdePrevia = async () => {
    await onDownload("excel");
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {previa && (
        <ModalVistaPrevia
          documento={documento}
          generado={generado}
          onClose={() => setPrevia(false)}
          onExcel={excelDesdePrevia}
        />
      )}
      <button
        type="button"
        onClick={() => setPrevia(true)}
        disabled={disabled || noData}
        title="Ver el reporte antes de descargarlo"
        className="inline-flex items-center gap-2 rounded border border-line bg-subtle px-3 py-1 text-xs font-medium text-content-secondary hover:bg-surface transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <i className="ri-eye-line" />
        <span className="hidden sm:inline">Vista previa</span>
      </button>
      <button
        type="button"
        onClick={() => onDownload("excel")}
        disabled={blocked}
        title="Descargar Excel (datos filtrados)"
        className="inline-flex items-center gap-2 rounded border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success hover:bg-success/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <i
          className={
            exporting === "excel"
              ? "ri-loader-4-line animate-spin"
              : "ri-file-excel-2-line"
          }
        />
        <span className="hidden sm:inline">
          {exporting === "excel" ? "Generando…" : "Excel"}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onDownload("pdf")}
        disabled={blocked}
        title="Descargar PDF (datos filtrados)"
        className="inline-flex items-center gap-2 rounded border border-error/30 bg-error/10 px-3 py-1 text-xs font-medium text-error hover:bg-error/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
      >
        <i
          className={
            exporting === "pdf"
              ? "ri-loader-4-line animate-spin"
              : "ri-file-pdf-2-line"
          }
        />
        <span className="hidden sm:inline">
          {exporting === "pdf" ? "Generando…" : "PDF"}
        </span>
      </button>
    </div>
  );
}
